import { useState } from 'react';
import { useIntl } from 'react-intl';
import {
  calcCurrentPageWithNum,
  calcIndexByOrder,
  removeFalsyKeys,
} from 'utils/common';
import { useCustomSearchParams } from 'hooks/useCustomSearchParams';
import { StyledActions, StyledButton } from 'styles/overrides';
import { Table } from 'components/table';
import { useUsers, useDeleteUser, useDeleteUserFeedback } from 'api/userApi';
import { Notification } from 'components/Notification';
import { formatTime } from 'utils/timeUtils';
import UserFilterSearch from '../UserFilterSearch/UserFilterSearch';
import { Modal, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { Publish } from 'components/common';
import { PATH_NAME_ACTION } from 'constants/routes';

export default function UserTable() {
  const intl = useIntl();
  const [search, setSearch] = useCustomSearchParams();
  const { data, isLoading, refetch } = useUsers({ params: search });
  const deleteUserMutation = useDeleteUser();
  const deleteUserMutationFeedback = useDeleteUserFeedback();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const columns = [
    {
      title: intl.formatMessage({ id: 'common.table.no' }),
      render: (text, record, index) => {
        return calcIndexByOrder({
          index,
          page: search.page,
          limit: search.page_size,
        });
      },
    },
    {
      title: intl.formatMessage({ id: 'views.users.table.fullName' }),
      dataIndex: 'full_name',
    },
    {
      title: intl.formatMessage({ id: 'views.users.table.name' }),
      dataIndex: 'user_name',
      render: (value) => <Typography.Text>{value}</Typography.Text>,
    },
    {
      title: intl.formatMessage({ id: 'views.users.table.email' }),
      dataIndex: 'email',
    },
    {
      title: intl.formatMessage({ id: 'views.users.table.phone' }),
      dataIndex: 'phone',
    },
    {
      title: intl.formatMessage({ id: 'views.users.table.branch' }),
      dataIndex: ['branch', 'address'],
    },
    {
      title: intl.formatMessage({ id: 'views.users.table.role' }),
      dataIndex: ['role', 'name'],
    },
    {
      title: intl.formatMessage({ id: 'common.table.published' }),
      dataIndex: 'published',
      render: (value) => <Publish published={value} />,
    },
    {
      title: intl.formatMessage({ id: 'common.table.createdAt' }),
      dataIndex: 'created_at',
      render: (value) => formatTime(value),
    },
    {
      render: ({ id }) => (
        <Link to={`${id}`}>
          <StyledButton type="primary">
            {intl.formatMessage({ id: 'common.btn.seeDetails' })}
          </StyledButton>
        </Link>
      ),
    },
  ];

  const handleDelete = () => {
    if (selectedRowKeys.length <= 0) return;

    Modal.confirm({
      title: intl.formatMessage({ id: 'common.modal.deleteTitle' }),
      okText: intl.formatMessage({ id: 'common.btn.confirm' }),
      onOk() {
        return deleteUserMutation.mutateAsync(selectedRowKeys, {
          onSuccess: (res) => {
            if (res) {
              deleteUserMutationFeedback.mutateAsync(selectedRowKeys, {
                onSuccess: () => {
                  const calculatedPage = calcCurrentPageWithNum({
                    page: search.page,
                    limit: search.page_size,
                    total: data?.meta?.total,
                    num: selectedRowKeys.length,
                  });

                  if (calculatedPage !== search.page) {
                    setSearch(
                      removeFalsyKeys({
                        ...search,
                        page: calculatedPage,
                      }),
                    );
                  } else {
                    refetch();
                  }

                  setSelectedRowKeys([]);
                  Notification(
                    'success',
                    intl.formatMessage({ id: 'message.deleteSuccess' }),
                  );
                },
                onError(error) {
                  Notification(
                    'error',
                    error?.message ||
                    intl.formatMessage({ id: 'message.commonError' }),
                  );
                },
              });
              return;
            }
          },
          onError(error) {
            Notification(
              'error',
              error?.message ||
              intl.formatMessage({ id: 'message.commonError' }),
            );
          },
        });
      },
      onCancel() { },
    });
  };

  const handleChangeTable = ({ current: page, pageSize: limit }) => {
    setSearch(removeFalsyKeys({ ...search, page, page_size: limit }));
  };

  return (
    <>
      <UserFilterSearch
        onSubmit={(values) => setSearch(values)}
        initialValues={search}
      />

      <StyledActions size={24}>
        <Link to={PATH_NAME_ACTION.CREATE}>
          <StyledButton type="primary">
            {intl.formatMessage({ id: 'common.btn.addNew' })}
          </StyledButton>
        </Link>

        <StyledButton
          onClick={handleDelete}
          disabled={selectedRowKeys.length === 0}
        >
          {intl.formatMessage({ id: 'common.btn.delete' })}
        </StyledButton>
      </StyledActions>

      <Table
        rowKey="id"
        rowSelection={{
          selectedRowKeys,
          onChange: (newSelectedRowKeys) => {
            setSelectedRowKeys(newSelectedRowKeys);
          },
          preserveSelectedRowKeys: true,
        }}
        columns={columns}
        dataSource={data?.data || []}
        loading={isLoading || deleteUserMutation.isLoading}
        pagination={{
          current: search?.page,
          pageSize: search?.page_size,
          total: data?.meta?.total || 0,
        }}
        onChange={handleChangeTable}
      />
    </>
  );
}
